/* eslint-disable */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  Chip,
  Stack,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { fetcher } from 'src/utils/axios';
import PODialog from './PODialog';

// Color scheme derived from the "Create Node Template" UI in the screenshot:
// - Background: #F5F5F5 (light gray content area)
// - Surface/card: #FFFFFF with subtle border #E0E0E0
// - Sidebar/dark accent: #1A1A2E (dark navy)
// - Primary action: #1A1A2E (dark navy button)
// - Text primary: #1A1A2E
// - Text secondary: #6B7280
// - Warning/alert: amber tones matching the dashed-border empty state feel

const styles = {
  paper: {
    borderRadius: '12px',
    backgroundColor: '#F5F5F5',
    border: '1px solid #E0E0E0',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  },
  title: {
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E8E8E8',
    px: 3,
    py: 2,
  },
  content: {
    backgroundColor: '#F5F5F5',
    px: 3,
    pt: 2,
  },
  alert: {
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFD54F',
    borderRadius: '8px',
    color: '#7C5A00',
    mb: 2,
    '& .MuiAlert-icon': { color: '#F59E0B' },
  },
  itemCard: {
    p: 2,
    bgcolor: '#FFFFFF',
    borderRadius: '8px',
    border: '1px solid #E0E0E0',
  },
  chip: {
    backgroundColor: '#F0F0F0',
    border: '1px solid #E0E0E0',
    color: '#4B5563',
    fontSize: '0.7rem',
    height: '22px',
  },
  statLabel: {
    variant: 'body2',
    color: '#6B7280',
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 500,
  },
  actions: {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E8E8E8',
    px: 3,
    py: 2,
    gap: 1,
  },
  cancelBtn: {
    color: '#374151',
    borderColor: '#D1D5DB',
    borderRadius: '8px',
    minWidth: 100,
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      borderColor: '#9CA3AF',
      backgroundColor: '#F9FAFB',
    },
  },
  confirmBtn: {
    backgroundColor: '#1A1A2E',
    color: '#FFFFFF',
    borderRadius: '8px',
    minWidth: 140,
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#2D2D4E',
    },
    '&:disabled': {
      backgroundColor: '#D1D5DB',
      color: '#9CA3AF',
    },
  },
};

export default function QuantityWarningDialog({
  open,
  onClose,
  report = [],
  jobId,
  onConfirm = () => {},
}) {
  const [openPODialog, setOpenPODialog] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [loadingVendors, setLoadingVendors] = useState(false);

  const hasShortage = report ? report.some((r) => r.shortage_qty > 0) : false;

  const safeReport = Array.isArray(report) ? report : [];
  console.log('QuantityWarningDialog report:', safeReport);
  const nodeids = safeReport.map((item) => item?.node_id);
  console.log('Node IDs in report:', nodeids);

  useEffect(() => {
    if (open && hasShortage) {
      const loadVendors = async () => {
        setLoadingVendors(true);
        try {
          const res = await fetcher(['products-app/vendor/dropdown/']);
          const data = res || [];
          setVendors(data);
          if (data.length > 0) {
            setSelectedVendor(data[0].id);
          }
        } catch (e) {
          console.error('Failed to load vendors:', e);
        } finally {
          setLoadingVendors(false);
        }
      };
      loadVendors();
    }
  }, [open, hasShortage]);

  const handleGeneratePO = () => {
    setOpenPODialog(true);
    onClose();
  };

  const handlePOClose = () => {
    setOpenPODialog(false);
    onConfirm?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.paper }}
      >
        <DialogTitle sx={styles.title}>
          <Typography
            variant="h6"
            fontWeight={600}
            fontSize="1rem"
            color="#1A1A2E"
            letterSpacing="-0.01em"
          >
            Quantity Warning
          </Typography>
        </DialogTitle>

        <DialogContent sx={styles.content}>
          <Alert severity="warning" sx={styles.alert}>
            <Typography variant="body2" fontWeight={500} color="#7C5A00">
              Insufficient Quantity Available
            </Typography>
          </Alert>

          {/* ------------------ SHORTAGE LIST ------------------ */}
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {safeReport.map((item, idx) => (
              <Box key={idx} sx={styles.itemCard}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="#1A1A2E"
                  fontSize="0.85rem"
                >
                  {item.product.name}
                </Typography>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1.5 }}>
                  {Object.entries(item.variant.attributes)
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <Chip key={k} label={`${k}: ${v}`} size="small" color='default' />
                    ))}
                </Stack>

                {/* Stat row */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 1,
                    pt: 1,
                    borderTop: '1px solid #F0F0F0',
                  }}
                >
                  {/* Required */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#9CA3AF',
                        fontWeight: 600,
                        mb: 0.25,
                      }}
                    >
                      Required
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: '#DC2626', fontSize: '0.9rem' }}
                    >
                      {item.required_qty}
                    </Typography>
                  </Box>
                  {/* Available */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#9CA3AF',
                        fontWeight: 600,
                        mb: 0.25,
                      }}
                    >
                      Available
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: '#16A34A', fontSize: '0.9rem' }}
                    >
                      {item.available_qty}
                    </Typography>
                  </Box>
                  {/* Short */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#9CA3AF',
                        fontWeight: 600,
                        mb: 0.25,
                      }}
                    >
                      Short
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: '#D97706', fontSize: '0.9rem' }}
                    >
                      {item.shortage_qty}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>

          <Typography variant="body2" fontWeight={500} color="#374151" sx={{ mt: 2 }}>
            Do you want to generate the Purchase Order (PO)?
          </Typography>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button onClick={onClose} variant="outlined" sx={styles.cancelBtn}>
            No
          </Button>
          <Button
            onClick={handleGeneratePO}
            variant="contained"
            disabled={!hasShortage || !selectedVendor}
            sx={styles.confirmBtn}
          >
            Yes, Generate PO
          </Button>
        </DialogActions>
      </Dialog>

      <PODialog
        open={openPODialog}
        onClose={handlePOClose}
        shortageReport={safeReport}
        initialVendorId={selectedVendor}
        jobId={jobId}
        nodeids={nodeids}
      />
    </>
  );
}

QuantityWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.arrayOf(
    PropTypes.shape({
      product: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
      variant: PropTypes.shape({
        attributes: PropTypes.object,
      }).isRequired,
      required_qty: PropTypes.number.isRequired,
      available_qty: PropTypes.number.isRequired,
      shortage_qty: PropTypes.number.isRequired,
      node_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onConfirm: PropTypes.func,
};